import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import MtiButton from "../Buttons/MtiButton";
import IconClose from "../../Icons/Close";
import "./Modal.css";

const ModalContext = React.createContext({
  onHideModal: (e: { stopPropagation: () => void }) => {},
});

type Props = {
  classNameBackdrop?: string;
  className?: string;
  show?: boolean;
  hideOnBackdropClick?: boolean;
  relativeToParent?: boolean;
  onHide?: () => void;
  styleBackdrop?: React.CSSProperties;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

const Modal = ({
  show = false,
  className = "",
  classNameBackdrop = "",
  hideOnBackdropClick = true,
  relativeToParent = false,
  onHide = () => {},
  style = {},
  styleBackdrop = {},
  children,
}: Props) => {
  classNameBackdrop =
    classNameBackdrop != null
      ? classNameBackdrop + " common-modal-backdrop"
      : "common-modal-backdrop";
  className =
    className != null ? className + " common-modal-main" : "common-modal-main";

  const modalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [relativeStyle, setRelativeStyle] = useState({});

  const onHideModal = useCallback(
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      onHide();
    },
    [onHide]
  );

  const onClickBody = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
  };

  const context = {
    onHideModal: onHideModal,
  };

  useEffect(() => {
    if (
      modalRef?.current != null &&
      backdropRef?.current?.parentElement != null &&
      relativeToParent
    ) {
      const parentBoundingRect =
        backdropRef.current.parentElement.getBoundingClientRect();
      let totalTop = parentBoundingRect.top + parentBoundingRect.height;
      let totalLeft = parentBoundingRect.left;

      setRelativeStyle({
        position: "absolute",
        top: totalTop,
        left: totalLeft,
        transform: "none",
      });
    }
  }, [relativeToParent, show]);

  useEffect(() => {
    const onEscKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Escape" && hideOnBackdropClick) {
        onHideModal(event);
      }
    };

    window.addEventListener("keydown", onEscKeyDown);
    return () => {
      window.removeEventListener("keydown", onEscKeyDown);
    };
  }, [onHideModal, hideOnBackdropClick]);

  return (
    <div
      className={show ? classNameBackdrop : classNameBackdrop + " inactive"}
      style={{
        ...styleBackdrop,
      }}
      onClick={(e) => {
        if (hideOnBackdropClick) {
          onHideModal(e);
        }
      }}
      ref={backdropRef}
    >
      <div
        className={className}
        style={{
          ...relativeStyle,
          ...style,
        }}
        onClick={(e) => onClickBody(e)}
        ref={modalRef}
      >
        <ModalContext.Provider value={context}>
          {children}
        </ModalContext.Provider>
      </div>
    </div>
  );
};

const useModalContext = () => {
  const context = useContext(ModalContext);
  if (context == null) {
    throw new Error(
      `Modal common compound components cannot be rendered outside the Modal common Component`
    );
  }

  return context;
};

type HeaderProps = {
  className?: string;
  title?: string;
  hideButtonEnabled?: boolean;
  style?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  children?: React.ReactNode;
};
const Header = ({
  className,
  title,
  hideButtonEnabled,
  style = {},
  children,
  titleStyle,
}: HeaderProps) => {
  const { onHideModal } = useModalContext();
  className =
    className != null
      ? className + " common-modal-header"
      : "common-modal-header";

  return (
    <div
      className={className}
      style={{
        ...style,
      }}
    >
      {title != null && <Title style={titleStyle} title={title} />}
      {hideButtonEnabled !== false && hideButtonEnabled !== null && (
        <CloseButton onClick={onHideModal} />
      )}
      {children}
    </div>
  );
};

type TitleProps = {
  className?: string;
  title: string;
  style?: React.CSSProperties;
};
const Title = ({ className, title, style = {} }: TitleProps) => {
  className =
    className != null
      ? className + " common-modal-header-title"
      : "common-modal-header-title";
  return (
    <div
      className={className}
      style={{
        ...style,
      }}
    >
      {title}
    </div>
  );
};

type CloseButtonProps = {
  className?: string;
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  style?: React.CSSProperties;
};
const CloseButton = ({ className, onClick, style = {} }: CloseButtonProps) => {
  className =
    className != null
      ? className + " common-modal-header-close"
      : "common-modal-header-close";

  return (
    <div
      className={className}
      style={{
        ...style,
      }}
      onClick={onClick}
    >
      <IconClose iconcolor={style?.color} />
    </div>
  );
};

type BodyProps = {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
};
const Body = ({ className, children, style = {} }: BodyProps) => {
  className =
    className != null ? "common-modal-body " + className : "common-modal-body";
  return (
    <div
      className={className}
      style={{
        ...style,
      }}
    >
      {children}
    </div>
  );
};

type FooterProps = {
  className?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  styleOkButton?: React.CSSProperties;
  styleCancelButton?: React.CSSProperties;
  onClickOk?: () => void;
  onClickCancel?: () => void;
  okButtonDisabled?: boolean;
  okLabel?: string;
  cancelLabel?: string;
};

const Footer = ({
  className,
  children,
  onClickOk,
  onClickCancel,
  okButtonDisabled = false,
  style = {},
  styleOkButton = {},
  styleCancelButton = {},
  okLabel = undefined,
  cancelLabel = undefined,
}: FooterProps) => {
  const { onHideModal } = useModalContext();
  className =
    className != null
      ? className + " common-modal-footer"
      : "common-modal-footer";

  if (onClickCancel != null) {
    className += " ok-cancel-container";
  }
  return (
    <div
      className={className}
      style={{
        ...style,
      }}
    >
      {onClickCancel != null && (
        <MtiButton
          variant="secondary"
          className={"cancel-button"}
          style={{
            ...styleCancelButton,
          }}
          onClick={(e) => {
            onClickCancel();
            onHideModal(e);
          }}
        >
          {cancelLabel ? cancelLabel : "Cancel"}
        </MtiButton>
      )}

      {onClickOk != null && (
        <MtiButton
          disabled={okButtonDisabled}
          className={"ok-button"}
          style={{
            ...styleOkButton,
          }}
          onClick={onClickOk}
        >
          {okLabel ? okLabel : "OK"}
        </MtiButton>
      )}
      {children}
    </div>
  );
};

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;
Modal.Title = Title;
Modal.HideButton = CloseButton;

export default Modal;
